import { useState, useRef, useEffect } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";

export interface SelectPickerOption {
  value: string;
  label: string;
}

interface SelectPickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectPickerOption[];
  placeholder?: string;
  disabled?: boolean;
  loadingLabel?: string;
  loading?: boolean;
  testId?: string;
  hasError?: boolean;
  /**
   * Direction the popover opens. Defaults to "down" (popover sits beneath the
   * trigger, arrow points up). Use "up" inside short modals where the popover
   * would otherwise overflow the viewport.
   */
  direction?: "up" | "down";
}

export function SelectPicker({
  id,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  loadingLabel,
  loading = false,
  testId,
  hasError = false,
  direction = "down",
}: SelectPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const selected = options.find((o) => o.value === value);
  const triggerLabel = loading
    ? (loadingLabel ?? "Loading…")
    : (selected?.label ?? placeholder);
  const isPlaceholder = !selected && !loading;

  const popoverPosition =
    direction === "down"
      ? "absolute left-0 top-full mt-2.5 z-[60] w-full rounded-xl bg-popover shadow-md"
      : "absolute left-0 bottom-full mb-2.5 z-[60] w-full rounded-xl bg-popover shadow-md";

  const arrowPosition =
    direction === "down"
      ? "absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-popover rotate-45 z-10"
      : "absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-popover rotate-45 z-10";

  const arrowBorderStyle =
    direction === "down"
      ? {
          borderLeft: "0.5px solid var(--hairline)",
          borderTop: "0.5px solid var(--hairline)",
        }
      : {
          borderRight: "0.5px solid var(--hairline)",
          borderBottom: "0.5px solid var(--hairline)",
        };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        id={id}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        data-testid={testId}
        className={`flex h-11 w-full items-center justify-between rounded-lg border-[0.5px] ${
          hasError ? "border-destructive" : "border-input"
        } bg-background px-3 py-2 text-sm shadow-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`}
      >
        <span className={isPlaceholder ? "text-muted-foreground" : "text-foreground"}>
          {triggerLabel}
        </span>
        <CaretDown size={16} className="opacity-50" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: direction === "down" ? -4 : 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: direction === "down" ? -4 : 4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={popoverPosition}
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            <div className={arrowPosition} style={arrowBorderStyle} />
            <div className="max-h-72 overflow-y-auto p-2">
              {options.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">
                  No options available
                </p>
              )}
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                >
                  <span className="flex-1 text-left">{option.label}</span>
                  {value === option.value && (
                    <Check size={14} className="text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
